import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, DataGrid, Select, TextField } from '../../design-system';
import { useAppContext } from '../../providers/AppContext';
import { listAllProjectSamples, updateSample } from '../../api/data';
import {
  CalculationStrategies,
  ProjectSample,
  Sample,
  UpdateSamplePayload,
} from '../../@types/samples/types';
import { Well } from '../../@types/wells/types';
import ProjectShell from '../../components/layout/ProjectShell';
import { buildColumns } from './columns';
import { EDITABLE_FIELDS } from './helpers';
import { useSamplesSummary } from './useSamplesSummary';
import { GroupMode, SummaryRow } from './types';
import styles from './SamplesSummaryPage.module.css';

const GROUP_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'well', label: 'Group by well' },
  { value: 'analysis', label: 'Group by analysis' },
  { value: 'well-analysis', label: 'Group by well + analysis' },
];

interface SamplesSummaryTableProps {
  samples: ProjectSample[];
  wells: Well[];
  loading: boolean;
  onSave: (payload: UpdateSamplePayload) => Promise<Sample>;
  onSaved: (updated: Sample) => void;
}

/** Adapt a raw API `Sample` into a `ProjectSample` for the AppContext store. */
const toProjectSample = (sample: Sample): ProjectSample => ({
  ...sample,
  performed_analyses: sample.performed_analyses ?? [],
  selectedAnalyses: Object.values(CalculationStrategies).reduce(
    (acc, strategy) => {
      acc[strategy] = false;
      return acc;
    },
    {} as Record<CalculationStrategies, boolean>,
  ),
  selected: false,
});

/** Presentational table — pure relative to its props, easy to unit test. */
export const SamplesSummaryTable = ({
  samples,
  wells,
  loading,
  onSave,
  onSaved,
}: SamplesSummaryTableProps) => {
  const summary = useSamplesSummary({ samples, wells, onSave, onSaved });
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [editUid, setEditUid] = useState<string | null>(null);
  const [editDirty, setEditDirty] = useState(false);
  const [commitEditSignal, setCommitEditSignal] = useState(0);

  const depthUnit = samples[0]?.depth_unit ?? 'ft';

  const columns = useMemo(
    () => buildColumns({ depthUnit }),
    [depthUnit],
  );

  const isEmpty = !loading && samples.length === 0;
  const selectedKeys = useMemo(
    () => (selectedUid ? new Set<string | number>([selectedUid]) : new Set<string | number>()),
    [selectedUid],
  );

  const selectRow = useCallback((uid: string | null) => {
    setSelectedUid(uid);
    setEditUid(current => (current === uid ? current : null));
    setEditDirty(false);
  }, []);

  const handleSelectionChange = useCallback(
    (keys: Set<string | number>) => {
      if (editUid) return;
      const candidates = Array.from(keys).map(String);
      const next = candidates.find(uid => uid !== selectedUid) ?? candidates[0] ?? null;
      selectRow(next);
    },
    [editUid, selectRow, selectedUid],
  );

  const handleRowSave = useCallback(
    async (row: SummaryRow) => {
      const original = summary.sampleByUid[row.uid];
      if (!original) return;
      setEditUid(null);
      setEditDirty(false);
      const edits = EDITABLE_FIELDS.reduce(
        (acc, field) => {
          const originalValue =
            field === 'sample_code' || field === 'depth'
              ? original[field]
              : original.input_sample_values?.[field];
          const nextValue = row[field];
          if (String(nextValue ?? '') !== String(originalValue ?? '')) {
            acc[field] = String(nextValue ?? '');
          }
          return acc;
        },
        {} as Partial<Record<(typeof EDITABLE_FIELDS)[number], string>>,
      );
      await summary.commitRow(original, edits);
    },
    [summary],
  );

  return (
    <div className={styles.tableArea}>
      <div className={styles.toolbar}>
        <TextField
          size="sm"
          placeholder="Search by sample code…"
          value={summary.search}
          onChange={event => summary.setSearch(event.target.value)}
          aria-label="Search by sample code"
        />
        <Select
          size="sm"
          className={styles.filterSelect}
          options={summary.wellOptions}
          value={summary.wellFilter}
          onChange={summary.setWellFilter}
          aria-label="Filter by well"
        />
        <Select
          size="sm"
          className={styles.groupSelect}
          options={GROUP_OPTIONS}
          value={summary.groupMode}
          onChange={value => summary.setGroupMode(value as GroupMode)}
          aria-label="Group samples"
        />
        <span className={styles.count}>
          {summary.filteredCount} of {summary.totalCount} samples
        </span>
        {selectedUid && !editUid && (
          <Button
            variant="secondary"
            size="sm"
            loading={summary.savingUid === selectedUid}
            onClick={() => {
              setEditDirty(false);
              setEditUid(selectedUid);
            }}
          >
            Click Here to Edit
          </Button>
        )}
        {editUid && editDirty && (
          <Button
            variant="primary"
            size="sm"
            loading={summary.savingUid === editUid}
            onClick={() => setCommitEditSignal(value => value + 1)}
          >
            Salvar edição
          </Button>
        )}
      </div>

      {summary.banner && (
        <div
          className={`${styles.banner} ${styles.bannerError}`}
          role="alert"
        >
          <span>{summary.banner.message}</span>
          <button type="button" className={styles.bannerClose} onClick={summary.dismissBanner}>
            ✕
          </button>
        </div>
      )}

      {isEmpty ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>No samples in this project yet</p>
          <p className={styles.emptySubtitle}>
            Import or create samples from an analysis page to see them summarized here.
          </p>
        </div>
      ) : (
        summary.groups.map(group => (
          <div key={group.key} className={styles.group}>
            {group.label && (
              <div className={styles.groupHeader}>
                {group.label}
                <span className={styles.groupCount}>
                  {group.rows.filter(r => !r.isAverage).length}
                </span>
              </div>
            )}
            <div className={styles.gridWrap}>
              <DataGrid<typeof group.rows[number]>
                columns={columns}
                data={group.rows}
                rowKey="uid"
                loading={loading}
                striped
                showIndex
                selectable
                selectedKeys={selectedKeys}
                onSelectionChange={handleSelectionChange}
                isRowSelectable={row => !row.isAverage}
                editableRows
                editOnRowClick={false}
                editRowKey={editUid}
                commitOnBlur={false}
                commitOnEnter={false}
                commitEditSignal={commitEditSignal}
                onEditChange={(_, __, dirty) => setEditDirty(dirty)}
                onRowSave={handleRowSave}
                defaultSort={summary.sort ?? undefined}
                onSortChange={summary.setSort}
                onRowClick={row => {
                  if (!row.isAverage && !editUid) selectRow(row.uid);
                }}
                maxHeight="calc(100vh - 160px)"
                emptyMessage="No samples match the current filters."
              />
            </div>
          </div>
        ))
      )}

    </div>
  );
};

const SamplesSummaryContent = () => {
  const { data, updateData, configStates, updateConfigStates } = useAppContext();
  const location = useLocation();
  const projectUid = new URLSearchParams(location.search).get('project');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!projectUid || loadedFor === projectUid) return;
    let cancelled = false;
    updateConfigStates({ loadingProjectSamples: true });
    listAllProjectSamples(projectUid)
      .then(samples => {
        if (cancelled) return;
        updateData({
          projectSamples: Array.isArray(samples) ? samples.map(toProjectSample) : [],
        });
        setLoadedFor(projectUid);
      })
      .catch(error => console.error('Error loading project samples:', error))
      .finally(() => {
        if (!cancelled) updateConfigStates({ loadingProjectSamples: false });
      });
    return () => {
      cancelled = true;
    };
  }, [projectUid, loadedFor, updateData, updateConfigStates]);

  const handleSaved = useCallback(
    (updated: Sample) => {
      updateData({
        projectSamples: data.projectSamples.map(sample =>
          sample.uid === updated.uid ? { ...sample, ...updated } : sample,
        ),
      });
    },
    [data.projectSamples, updateData],
  );

  if (!projectUid) {
    return (
      <div className={styles.notFound}>
        <p className={styles.notFoundTitle}>No project selected</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Samples Summary</h1>
      </header>

      <SamplesSummaryTable
        samples={data.projectSamples}
        wells={data.wells}
        loading={configStates.loadingProjectSamples}
        onSave={updateSample}
        onSaved={handleSaved}
      />
    </div>
  );
};

const SamplesSummaryPage = () => (
  <ProjectShell>
    <SamplesSummaryContent />
  </ProjectShell>
);

export default SamplesSummaryPage;
